import { post, get, put } from './client'
import { User, AuthTokens } from '@/store/auth'

// Login/Register Request Types
export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
  phone?: string
}

export interface PasswordChangeRequest {
  current_password: string
  new_password: string
}

export interface ProfileUpdateRequest {
  name: string
  phone?: string
}

// Response Types
export interface LoginResponse {
  user: User
  tokens: {
    access_token: string
    refresh_token: string
    token_type: string
    expires_in: number
  }
  must_change_password?: boolean
}

export interface RegisterResponse {
  message: string
  user: User
}

export interface RefreshTokenRequest {
  refresh_token: string
}

export interface TokenValidationResponse {
  valid: boolean
  user?: User
}

// Authentication API Service
export const authApi = {
  // Login user
  login: (credentials: LoginRequest): Promise<LoginResponse> => {
    return post<LoginResponse>('/api/v1/auth/login', credentials)
  },

  // Register new user
  register: (userData: RegisterRequest): Promise<RegisterResponse> => {
    return post<RegisterResponse>('/api/v1/auth/register', userData)
  },

  // Refresh access token
  refresh: (refreshData: RefreshTokenRequest): Promise<AuthTokens> => {
    return post<AuthTokens>('/api/v1/auth/refresh', refreshData)
  },

  // Logout user
  logout: (): Promise<{ message: string }> => {
    return post<{ message: string }>('/api/v1/auth/logout')
  },

  // Get current user info
  me: (): Promise<User> => {
    return get<User>('/api/v1/auth/me')
  },

  // Validate token
  validate: (): Promise<TokenValidationResponse> => {
    return get<TokenValidationResponse>('/api/v1/auth/validate')
  },

  // Change password
  changePassword: (passwordData: PasswordChangeRequest): Promise<{ message: string }> => {
    return put<{ message: string }>('/api/v1/auth/change-password', passwordData)
  },

  // Update profile
  updateProfile: (profileData: ProfileUpdateRequest): Promise<User> => {
    return put<User>('/api/v1/auth/profile', profileData)
  },

  // Request password reset
  requestPasswordReset: (email: string): Promise<{ message: string }> => {
    return post<{ message: string }>('/api/v1/auth/forgot-password', { email })
  },

  // Reset password with token
  resetPassword: (token: string, newPassword: string): Promise<{ message: string }> => {
    return post<{ message: string }>('/api/v1/auth/reset-password', {
      token,
      new_password: newPassword,
    })
  },

  // Check if email exists
  checkEmail: (email: string): Promise<{ exists: boolean }> => {
    return post<{ exists: boolean }>('/api/v1/auth/check-email', { email })
  },

  // Set initial password (first login, no current password needed)
  setInitialPassword: (newPassword: string): Promise<{ message: string }> => {
    return post<{ message: string }>('/api/v1/auth/set-initial-password', { new_password: newPassword })
  },
}

export default authApi 